import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  BrandingWatermark as WatermarkIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { generateUnifiedInvoiceHTML } from '../../utils/unifiedInvoiceTemplate';

const ViewInvoiceModal = ({ show, onClose, invoice, onConfirm }) => {
  const iframeRef = useRef(null);
  const navigate = useNavigate();
  const { currentSubscription, isPaidUser: contextIsPaid, getCurrentPlan, plans } = useSubscription();

  const [watermarkType, setWatermarkType] = useState('none');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [error, setError] = useState(null);

  const currentPlan = (typeof getCurrentPlan === 'function' ? getCurrentPlan() : currentSubscription?.plan) ||
    (plans && currentSubscription ? plans.find(p => p.id === (currentSubscription.plan_id || currentSubscription.planId)) : null);

  const isPaidUser = Boolean(
    contextIsPaid ||
    (
      currentSubscription &&
      (currentSubscription.status === 'active' || currentSubscription.is_lifetime) &&
      (
        currentSubscription.is_lifetime ||
        (currentPlan && (
          Number(currentPlan.price) > 0 ||
          currentPlan.name === '6 Months' ||
          currentPlan.name === '1 Month' ||
          currentPlan.name === '1 Year' ||
          currentPlan.name === '10 Years' ||
          currentPlan.name?.toLowerCase().includes('month') ||
          currentPlan.name?.toLowerCase().includes('year')
        )) ||
        (currentSubscription.plan_id && Number(currentSubscription.plan_id) > 2) ||
        (currentSubscription.planId && Number(currentSubscription.planId) > 2)
      )
    )
  );

  useEffect(() => {
    if (invoice && (invoice.watermarkType || invoice.watermark)) {
      setWatermarkType(invoice.watermarkType || invoice.watermark);
    }
  }, [invoice]);

  const handleWatermarkChange = (newVal) => {
    const diwaliLayouts = ['diwali_frame', 'diwali_diya', 'diwali_toran', 'diwali_rangoli'];
    if (diwaliLayouts.includes(newVal) && !isPaidUser) {
      setShowUpgradeModal(true);
      return;
    }
    setWatermarkType(newVal);
  };

  useEffect(() => {
    if (show && invoice) {
      try {
        // Transform the invoice data to robustly match the expected format for unifiedInvoiceTemplate
        const transformedInvoice = {
          company: {
            name: invoice.company?.name || invoice.company_name || '',
            address: invoice.company?.address || invoice.company_address || '',
            state: invoice.company?.state || invoice.company_state || '',
            mobile: invoice.company?.mobile || invoice.company_mobile || '',
            gstin: invoice.company?.gstin || invoice.company_gstin || '',
            email: invoice.company?.email || invoice.company_email || '',
            logo: invoice.company?.logo || invoice.company_logo || null
          },
          billTo: {
            name: invoice.billTo?.name || invoice.bill_to_name || '',
            address: invoice.billTo?.address || invoice.bill_to_address || '',
            state: invoice.billTo?.state || invoice.bill_to_state || '',
            mobile: invoice.billTo?.mobile || invoice.bill_to_mobile || '',
            gstin: invoice.billTo?.gstin || invoice.bill_to_gstin || ''
          },
          shipTo: {
            name: invoice.shipTo?.name || invoice.ship_to_name || invoice.billTo?.name || invoice.bill_to_name || '',
            address: invoice.shipTo?.address || invoice.ship_to_address || invoice.billTo?.address || invoice.bill_to_address || '',
            state: invoice.shipTo?.state || invoice.ship_to_state || invoice.billTo?.state || invoice.bill_to_state || '',
            mobile: invoice.shipTo?.mobile || invoice.ship_to_mobile || invoice.billTo?.mobile || invoice.bill_to_mobile || '',
            gstin: invoice.shipTo?.gstin || invoice.ship_to_gstin || invoice.billTo?.gstin || invoice.bill_to_gstin || ''
          },
          items: (() => {
            let items = [];
            if (Array.isArray(invoice.items)) {
              items = invoice.items;
            } else if (typeof invoice.items === 'string') {
              try {
                items = JSON.parse(invoice.items);
              } catch (e) {
                console.error('Failed to parse items string:', e);
                items = [];
              }
            }

            return items.map((item, index) => {
              const itemName = item.name || 
                            item.description || 
                            item.item_name || 
                            item.product_name || 
                            item.title || 
                            item.item_description || 
                            `Item ${index + 1}`;

              return {
                name: itemName,
                description: itemName,
                quantity: Number(item.quantity || item.qty || 0),
                rate: Number(item.rate || item.price || item.unit_price || 0),
                tax: Number(item.gst ?? item.tax ?? item.tax_rate ?? 0),
                discount: Number(item.discount || item.discount_percentage || 0),
                amount: Number(item.amount || item.total || 0),
                hsn: item.hsn || item.hsn_code || '',
                unit: item.unit || 'PCS'
              };
            });
          })(),
          subtotal: parseFloat(invoice.subtotal) || parseFloat(invoice.taxDetails?.taxableAmount) || 0,
          totalAmount: parseFloat(invoice.total) || parseFloat(invoice.totalAmount) || parseFloat(invoice.taxDetails?.total) || 0,
          total_discount: parseFloat(invoice.total_discount ?? invoice.totalDiscount) || 0,
          totalDiscount: parseFloat(invoice.total_discount ?? invoice.totalDiscount) || 0,
          taxDetails: {
            cgst: parseFloat(invoice.taxDetails?.cgst ?? invoice.cgst) || 0,
            sgst: parseFloat(invoice.taxDetails?.sgst ?? invoice.sgst) || 0,
            igst: parseFloat(invoice.taxDetails?.igst ?? invoice.igst) || 0,
            isInterstate: Boolean(invoice.taxDetails?.isInterstate || invoice.isInterstate),
            totalTax: parseFloat(invoice.taxDetails?.totalTax ?? invoice.gst) || 0
          },
          cgst: parseFloat(invoice.taxDetails?.cgst ?? invoice.cgst) || 0,
          sgst: parseFloat(invoice.taxDetails?.sgst ?? invoice.sgst) || 0,
          igst: parseFloat(invoice.taxDetails?.igst ?? invoice.igst) || 0,
          gst: parseFloat(invoice.taxDetails?.totalTax ?? invoice.gst) || 0,
          invoiceNumber: invoice.invoice_number || invoice.invoiceNumber || invoice.invoice_id || 'DRAFT',
          invoiceDate: invoice.invoice_date || invoice.invoiceDate || new Date().toISOString().split('T')[0],
          documentType: invoice.document_type || invoice.documentType || 'invoice',
          originalFor: invoice.original_for || invoice.originalFor || '',
          termsAndConditions: invoice.terms || invoice.termsAndConditions || '',
          amountInWords: invoice.amount_in_words || invoice.amountInWords || '',
          theme: invoice.theme || 'classic',
          watermarkType: watermarkType,
          watermark: watermarkType,
          bankDetails: {
            name: invoice.bankDetails?.name || invoice.bank_name || '',
            accountNo: invoice.bankDetails?.accountNo || invoice.bank_account_number || invoice.bank_account || '',
            ifscCode: invoice.bankDetails?.ifscCode || invoice.bank_ifsc_code || invoice.bank_ifsc || '',
            branch: invoice.bankDetails?.branch || invoice.bank_branch || '',
            holderName: invoice.bankDetails?.holderName || invoice.bank_holder_name || ''
          },
          transportDetails: {
            vehicleNumber: invoice.transportDetails?.vehicleNumber || invoice.vehicle_number || '',
            vehicleType: invoice.transportDetails?.vehicleType || invoice.vehicle_type || '',
            driverName: invoice.transportDetails?.driverName || invoice.driver_name || '',
            driverContact: invoice.transportDetails?.driverContact || invoice.driver_contact || '',
            ewayBillNumber: invoice.transportDetails?.ewayBillNumber || invoice.eway_bill_number || ''
          },
          signature: invoice.signature || invoice.signatureUrl || ''
        };

        const html = generateUnifiedInvoiceHTML(transformedInvoice);
        setHtmlContent(html);
        setError(null);
      } catch (err) {
        console.error('Error generating invoice preview:', err);
        setError('Error generating invoice preview: ' + err.message);
      }
    }
  }, [show, invoice, watermarkType]);

  const handlePrint = () => {
    if (iframeRef.current && iframeRef.current.contentWindow) {
      try {
        iframeRef.current.contentWindow.focus();
        iframeRef.current.contentWindow.print();
      } catch (err) {
        console.error('Failed to print from iframe:', err);
        window.print();
      }
    }
  };

  const handleConfirmSave = () => {
    if (onConfirm) {
      onConfirm(watermarkType);
    }
  };

  if (!invoice) return null;

  const invoiceNum = invoice.invoice_number || invoice.invoiceNumber || invoice.invoice_id;

  return (
    <>
      <Dialog
        open={Boolean(show)}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: { xs: '96vh', sm: '92vh' },
            maxHeight: { xs: '96vh', sm: '92vh' },
            borderRadius: '12px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            m: { xs: 1, sm: 2 }
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          py: 1.5,
          px: { xs: 2, sm: 3 },
          bgcolor: '#ffffff',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0f172a' }}>
              Invoice Preview
            </Typography>
            {invoiceNum && (
              <Typography variant="caption" sx={{ bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: '4px', fontWeight: 600, color: '#475569' }}>
                #{invoiceNum}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {/* Watermark Selector */}
            <FormControl size="small" sx={{ minWidth: { xs: 140, sm: 190 } }}>
              <InputLabel id="view-watermark-label" sx={{ fontSize: '0.85rem' }}>Watermark / Frame</InputLabel>
              <Select
                labelId="view-watermark-label"
                value={watermarkType}
                onChange={(e) => handleWatermarkChange(e.target.value)}
                label="Watermark / Frame"
                startAdornment={<WatermarkIcon sx={{ ml: 0.5, mr: 0.5, color: 'text.secondary', fontSize: 16 }} />}
                renderValue={(selected) => {
                  if (selected === 'diwali_frame') return 'Diwali Festive Frame';
                  if (selected === 'diwali_diya' || selected === 'diwali_wishes') return 'Diwali Wishes Watermark';
                  if (selected === 'diwali_toran') return 'Diwali Royal Toran';
                  if (selected === 'diwali_rangoli') return 'Diwali Shubh Labh Rangoli';
                  if (selected === 'center') return 'Center Large';
                  if (selected === 'stylized') return 'Elegant Stylized';
                  return 'None';
                }}
                sx={{
                  borderRadius: '8px',
                  bgcolor: '#fff',
                  fontSize: { xs: '0.8rem', sm: '0.875rem' },
                  height: 36
                }}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="center">Center Large</MenuItem>
                <MenuItem value="stylized">Elegant Stylized</MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  value="diwali_frame"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 0.8
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <span style={{ fontSize: '1.1rem' }}>🪔</span>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309', fontSize: '0.85rem' }}>
                        Diwali Festive Frame
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.7rem', display: 'block' }}>
                        Royal Gold Frame & Upright Diyas
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={isPaidUser ? "VIP" : "PRO"}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: isPaidUser ? '#dcfce7' : '#fef3c7',
                      color: isPaidUser ? '#166534' : '#b45309',
                      border: '1px solid',
                      borderColor: isPaidUser ? '#bbf7d0' : '#fde68a'
                    }}
                  />
                </MenuItem>
                {/* Temporarily commented out due to bug in center wishes watermark
                <MenuItem
                  value="diwali_diya"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 0.8
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <span style={{ fontSize: '1.1rem' }}>✨</span>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309', fontSize: '0.85rem' }}>
                        Diwali Wishes Watermark
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.7rem', display: 'block' }}>
                        Company Wishes Happy Diwali Highlight
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={isPaidUser ? "VIP" : "PRO"}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: isPaidUser ? '#dcfce7' : '#fef3c7',
                      color: isPaidUser ? '#166534' : '#b45309',
                      border: '1px solid',
                      borderColor: isPaidUser ? '#bbf7d0' : '#fde68a'
                    }}
                  />
                </MenuItem>
                */}
                <MenuItem
                  value="diwali_toran"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 0.8
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <span style={{ fontSize: '1.1rem' }}>🏮</span>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309', fontSize: '0.85rem' }}>
                        Diwali Royal Toran
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.7rem', display: 'block' }}>
                        Festive Toran & Hanging Diyas
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={isPaidUser ? "VIP" : "PRO"}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: isPaidUser ? '#dcfce7' : '#fef3c7',
                      color: isPaidUser ? '#166534' : '#b45309',
                      border: '1px solid',
                      borderColor: isPaidUser ? '#bbf7d0' : '#fde68a'
                    }}
                  />
                </MenuItem>
                <MenuItem
                  value="diwali_rangoli"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 0.8
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <span style={{ fontSize: '1.1rem' }}>𑁍</span>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#b45309', fontSize: '0.85rem' }}>
                        Diwali Shubh Labh Rangoli
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#78350f', fontSize: '0.7rem', display: 'block' }}>
                        Auspicious Rangoli & Shubh-Labh
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={isPaidUser ? "VIP" : "PRO"}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: isPaidUser ? '#dcfce7' : '#fef3c7',
                      color: isPaidUser ? '#166534' : '#b45309',
                      border: '1px solid',
                      borderColor: isPaidUser ? '#bbf7d0' : '#fde68a'
                    }}
                  />
                </MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#cbd5e1',
                color: '#334155',
                height: 36,
                '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' }
              }}
            >
              Print
            </Button>
            <IconButton onClick={onClose} size="small" sx={{ color: '#64748b' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, flex: 1, bgcolor: '#f8fafc', display: 'flex', overflow: 'hidden' }}>
          {error ? (
            <Box sx={{ p: 3, color: 'error.main' }}>
              <Typography variant="subtitle1" fontWeight={600}>Failed to generate preview</Typography>
              <Typography variant="body2">{error}</Typography>
            </Box>
          ) : (
            <iframe
              ref={iframeRef}
              srcDoc={htmlContent}
              title="Invoice Preview"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: '#f8fafc'
              }}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ py: 1.5, px: 3, bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', justifyContent: 'space-between' }}>
          <Button variant="outlined" color="inherit" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {onConfirm && (
              <Button
                variant="contained"
                onClick={handleConfirmSave}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  bgcolor: '#0f172a',
                  color: '#ffffff',
                  '&:hover': { bgcolor: '#1e293b' }
                }}
              >
                Confirm & Save Invoice
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Premium Upgrade Modal for Festive Watermarks */}
      <Dialog
        open={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 1,
            textAlign: 'center',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: '#fef3c7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
            mx: 'auto',
            mb: 2,
            boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)'
          }}>
            🪔
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#92400e', mb: 1 }}>
            Diwali Festive Frame (PRO)
          </Typography>
          <Typography variant="body2" sx={{ color: '#4b5563', lineHeight: 1.6, mb: 2.5 }}>
            Diwali special borders, glowing corner diyas, and auspicious festival watermarks are exclusive to <strong>Hamara BillBook Paid Subscribers</strong>.
          </Typography>
          <Box sx={{
            bgcolor: '#fffbeb',
            border: '1px solid #fde68a',
            borderRadius: '10px',
            p: 1.5,
            mb: 2.5,
            textAlign: 'left'
          }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', mb: 0.5 }}>
              ⭐ What’s included:
            </Typography>
            <Typography variant="caption" sx={{ color: '#78350f', display: 'block' }}>
              • Ornate Diwali decorative page borders & corner diyas<br/>
              • Auspicious "शुभ दीपावली" sacred mandala watermark<br/>
              • Printable high-resolution festive PDFs for your clients
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                setShowUpgradeModal(false);
                navigate('/subscription');
              }}
              sx={{
                bgcolor: '#d97706',
                color: '#fff',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '10px',
                py: 1.2,
                '&:hover': { bgcolor: '#b45309' }
              }}
            >
              Upgrade to Premium
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={() => setShowUpgradeModal(false)}
              sx={{ textTransform: 'none', color: '#6b7280', fontSize: '0.85rem' }}
            >
              Maybe Later
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ViewInvoiceModal;